import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, inject, input, Input, OnChanges, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatAutocomplete, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatOption } from "@angular/material/core";
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from "@angular/material/datepicker";
import { MatDivider } from "@angular/material/divider";
import { MatFormField, MatLabel, MatSuffix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatTooltip } from "@angular/material/tooltip";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { map, Observable, startWith } from "rxjs";
import { EpfService } from "src/app/services/epf.service";
import { ENVIRONMENT } from "../../../environments/environment";
import { Module } from "../../classes/flow/base/Module";
import { EnergyConsult } from "../../classes/flow/request/EnergyConsult";
import { RequestState, RequestStates } from "../../classes/flow/request/RequestStates";
import { Coach } from "../../classes/flow/session/impl/Coach";
import { ModuleDirective } from "../../directives/module-directive";
import { RoleDirective } from "../../directives/role-directive";
import { LocalDatePipe } from "../../pipes/date.pipe";
import { PostalcodePipe } from "../../pipes/postalcode.pipe";
import { ApplicationService } from "../../services/application.service";
import { AuthService } from "../../services/auth.service";
import { BookmarkService } from "../../services/bookmark.service";
import { CoachEnergyConsultService } from "../../services/coach-energy-consult.service";
import { DialogService } from "../../services/dialog.service";
import { EnergyConsultService } from "../../services/energy-consult.service";
import { SnackbarService } from "../../services/snackbar.service";
import { CustomTimepickerComponent } from "../custom-timepicker/custom-timepicker.component";
import { InputErrorComponent } from "../error/error.component";
import { StateProgressBarComponent } from "../state-progress-bar/state-progress-bar.component";
import { convertDate } from "src/app/helpers/convertDate";

export enum Action {
  ALL,
  OPEN,
  NONE,
}

@Component({
  selector: "app-energy-consult[energyConsult]",
  templateUrl: "./energy-consult.component.html",
  styleUrls: ["./energy-consult.component.less"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIcon,
    StateProgressBarComponent,
    ModuleDirective,
    RoleDirective,
    MatDivider,
    NgTemplateOutlet,
    MatButton,
    RouterLink,
    MatIconButton,
    MatTooltip,
    MatCard,
    MatCardContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    InputErrorComponent,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    CustomTimepickerComponent,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    AsyncPipe,
    TranslatePipe,
    LocalDatePipe,
    PostalcodePipe,
  ],
})
export class EnergyConsultComponent implements OnChanges {
  readonly applicationService = inject(ApplicationService);
  private readonly energyConsultService = inject(EnergyConsultService);
  readonly dialogService = inject(DialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbar = inject(SnackbarService);
  readonly bookmarkService = inject(BookmarkService);
  readonly dialog = inject(DialogService);
  private readonly application = inject(ApplicationService);
  private readonly coachEnergyConsultService = inject(CoachEnergyConsultService);
  private translateService = inject(TranslateService);

  public auth = inject(AuthService);
  public epf = inject(EpfService);
  public requestStates = input.required<RequestState[]>();

  private translate = inject(TranslateService);
  @Output() initRequests: EventEmitter<unknown> = new EventEmitter();

  @Input("energyConsult")
  public energyConsult!: EnergyConsult;
  public energyConsultId?: string;

  @Input("inspecting")
  public inspecting?: boolean;

  @Input("actions")
  public actions = Action.ALL;

  @ViewChild("deleteDialog")
  public deleteDialog!: TemplateRef<unknown>;

  @ViewChild("cancelDialog")
  public cancelDialog!: TemplateRef<unknown>;

  @ViewChild("noteDialog")
  public noteDialog!: TemplateRef<unknown>;

  //TODO should be put into generic component and used in this and coach-energyconsult component
  public formGroup = new FormGroup({
    date: new FormControl(new Date(), Validators.required),
    time: new FormControl("", Validators.required),
  });
  public changeCoachControl = new FormControl();
  public filteredOptions: Observable<Coach[]> | null = null;
  public coaches = input.required<Coach[]>();

  @Input("mapView")
  public mapView = false;

  public isEditingDate = false;

  public maxDate: Date;

  public noteTooltip: string | null = null;
  public hasTimeSlotDaily: string | null = null;

  //till this point
  public constructor() {
    this.maxDate = new Date();
    this.maxDate.setFullYear(new Date().getFullYear() + 1);
  }

  public ngOnChanges(): void {
    this.hasTimeSlotDaily = null;
    this.energyConsultId = this.route.snapshot.paramMap.get("id") ?? "";

    this.noteTooltip = this.energyConsult.extraProperties?.note ?? null;
    if (this.noteTooltip && this.noteTooltip.length > 100) {
      this.noteTooltip = this.noteTooltip?.substring(0, 100) + "...";
    }

    this.filteredOptions = this.changeCoachControl.valueChanges.pipe(
      startWith(""),
      map((value) => {
        const name = typeof value === "string" ? value : value?.name;
        return name ? this._filter(name as string) : this.coaches().slice();
      }),
    );

    for (let index = 0; index < (this.energyConsult.extraProperties?.timeSlotDaily?.length ?? 0); index++) {
      const timeSlot = this.energyConsult.extraProperties?.timeSlotDaily?.at(index);
      if (!timeSlot) continue;
      if (this.hasTimeSlotDaily === null) this.hasTimeSlotDaily = "";

      if (this.hasTimeSlotDaily !== "" && timeSlot !== "0") {
        this.hasTimeSlotDaily += ", ";
      }
      if (index === 5) {
        if (timeSlot === "1") {
          this.hasTimeSlotDaily += this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.NONE");
        }
      } else {
        if (timeSlot === "1") {
          this.hasTimeSlotDaily += this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.DAYS." + index);
          if (this.translate.currentLang() === "en") this.hasTimeSlotDaily += " ";
          this.hasTimeSlotDaily +=
            this.translate.currentLang() === "en"
              ? this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.MORNING")
              : this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.MORNING").toLowerCase();
        }
        if (timeSlot === "2") {
          this.hasTimeSlotDaily += this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.DAYS." + index);
          if (this.translate.currentLang() === "en") this.hasTimeSlotDaily += " ";
          this.hasTimeSlotDaily +=
            this.translate.currentLang() === "en"
              ? this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.AFTERNOON")
              : this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.AFTERNOON").toLowerCase();
        }
        if (timeSlot === "3") {
          this.hasTimeSlotDaily += `${this.translate.instant("COMPONENTS.TIME_SLOTS_DAILY.DAYS." + index)}`;
        }
      }
    }
  }

  /**
   * Checks if a energyConsult can be deleted
   */
  public get energyConsultCanBeDeleted() {
    return !this.checkAction(2) && this.energyConsult.canBeDeleted && this.auth.roleName === "resident";
  }

  /**
   * Checks if a energyConsult can be canceled
   */
  public get energyConsultCanBeCanceled() {
    return (
      !this.checkAction(2) &&
      this.energyConsult.canBeCanceled &&
      (this.auth.roleName === "resident" || this.energyConsult.state.name != RequestStates.NEW)
    );
  }

  /**
   * Gets the current state of the energyConsult
   */
  public get currentState() {
    return this.energyConsult.state.name;
  }

  /**
   * Open a cancel energyConsult Dialog
   * @param energyConsultId The id of the energyConsult
   */
  public cancelRequestDialog(energyConsultId: number) {
    this.dialogService.open({
      template: this.cancelDialog,
      data: {
        id: energyConsultId,
      },
    });
  }

  /**
   * Open a delete energyConsult Dialog
   * @param energyConsultId The id of the energyConsult
   */
  public deleteRequestDialog(energyConsultId: number): void {
    this.dialogService.open({
      template: this.deleteDialog,
      data: {
        id: energyConsultId,
      },
    });
  }

  /**
   * Cancel a energyConsult
   */
  public async cancelRequest() {
    // const oldState = this.energyConsult.state.name;
    this.energyConsult = await this.energyConsultService.setState(this.energyConsult, RequestStates.CANCELED, this.requestStates());
    // if (Messages.length > 0) {
    // Messages.forEach((message: { message: string }) => {
    //   console.error(message);
    //   this.snackbar.open(message.message);
    // });
    // this.energyConsult.setState(oldState, this.requestStates());
    // } else {
    this.initRequests.emit();
    // }
  }

  /**
   * Delete a energyConsult
   */
  public async deleteRequest(): Promise<void> {
    const payload = this.energyConsult.duplicate();
    if (payload.canBeDeleted && payload.state.name == RequestStates.NEW && !this.checkAction(Action.NONE)) {
      await this.energyConsultService.delete(this.energyConsult);
      this.initRequests.emit();
      return;
    } else if (payload.canBeDeleted && !this.checkAction(Action.NONE)) {
      payload.setState(RequestStates.PENDING_REJECT, this.requestStates());
    } else if (this.auth.roleName === "resident") {
      payload.setState(RequestStates.PENDING_DELETED, this.requestStates());
    }

    const result = await this.epf.updateEnergyConsult(this.energyConsult);
    this.energyConsult = result;
    this.initRequests.emit();
  }

  /**
   * Checks if the given number is equals to the current action
   * @param number The number of the action
   * @returns True if the number is equals to the current action, otherwise false
   */
  public checkAction(number: number): boolean {
    return this.actions == number;
  }

  displayCoachName(): string | null {
    const coach = this.energyConsult.coach;
    const firstName = coach?.firstName;
    const lastName = coach?.lastName;
    if (!coach) return null;
    if (!firstName) return null;
    if (!lastName) return null;

    return `${firstName} ${lastName}`;
  }

  displayResidentName(): string | null {
    const resident = this.energyConsult.resident;
    const firstName = resident?.firstName;
    const lastName = resident?.lastName;
    if (!resident) return null;
    if (!firstName) return null;
    if (!lastName) return null;

    return `${firstName} ${lastName}`;
  }

  public includesModule(module: Module): boolean {
    return ENVIRONMENT.MODULES.includes(module);
  }

  public async toggleBookmark(): Promise<void> {
    this.energyConsult.bookmarked = !this.energyConsult.bookmarked;
    await this.bookmarkService.setBookmark(this.energyConsult.id, this.energyConsult.bookmarked ? "add" : "remove");
  }

  public showNoteDialog(): void {
    this.dialogService.open({
      template: this.noteDialog,
      data: {
        note: this.energyConsult.extraProperties?.note,
      },
    });
  }

  //TODO should be put into generic component and used in this and coach-energyconsult component
  public setEditingDate(isEditingDate: boolean): void {
    console.log("setEditingDate", isEditingDate);
    this.isEditingDate = isEditingDate;
  }

  public fillInPreferredTimeSlot(index: number) {
    const startTime = this.energyConsult?.extraProperties?.preferredTimeSlots?.at(index)?.startTime;
    const time = startTime ? startTime.slice(11, 16) : null;
    const date = startTime ? new Date(startTime) : null;
    try {
      this.formGroup.controls["date"].setValue(date);
      this.formGroup.controls["time"].setValue(time);
      this.formGroup.controls["date"].markAsTouched();
      this.formGroup.controls["time"].markAsTouched();
    } catch {
      // this.snackbarService.error();
    }
  }

  public async saveDate() {
    const date: Date = this.getGivenDate();
    try {
      if (this.energyConsult) {
        this.energyConsult = await this.coachEnergyConsultService.saveDate(this.energyConsult, date, this.requestStates());
      }
    } catch (error) {
      this.isEditingDate = false;
      this.energyConsult.appointmentDate = convertDate(date);
      this.snackbar.error();
    } finally {
      this.application.setLoading(false);
    }
  }
  private getGivenDate(): Date {
    const date: Date = this.formGroup.value.date!;
    date.setHours(+this.formGroup.value.time!.substr(0, 2), +this.formGroup.value.time!.substr(3, 2));
    return date;
  }

  public async assignCoach(coach: Coach) {
    try {
      this.application.setLoading(true);
      if (this.energyConsult) {
        this.energyConsult = await this.coachEnergyConsultService.AcceptEnergyConsult(this.energyConsult, coach, this.requestStates());
        // this.initEnergyConsult(this.energyConsult.id);
        this.dialog.close();
      }
    } catch (error) {
      this.snackbar.open(this.translateService.instant("COMPONENTS.EVERYWHERE.ERROR.CHANGE_COACH"));
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }
  public async changeCoachClickHandler(template: TemplateRef<any>) {
    this.changeCoachControl.patchValue(this.energyConsult?.coach);
    this.dialog.show(template);
  }

  public changeDisplayCoachName(coach: Coach) {
    if (coach) return coach.firstName + " " + coach.lastName;
    return "";
  }

  private _filter(name: string): Coach[] {
    const filterValue = name.toLowerCase();

    return this.coaches().filter(
      (coach: Coach) => coach.firstName?.toLowerCase().includes(filterValue) || coach.lastName?.toLowerCase().includes(filterValue),
    );
  }
}
